import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, Plus, History } from 'lucide-react';

import { useDateTime } from '../contexts/DateTimeContext';
import { documentsApi } from '../services/api';
import BlockNoteEditor from './LazyBlockNoteEditor';
import { Button } from './ui/button';

/**
 * ChangelogView - chronological feed of "changelog" documents in a project.
 *
 * A changelog entry is a `Document` with `type === 'changelog'` (rich-text
 * Tiptap content). This view renders entries grouped by month, newest first,
 * matching the visual style of public product changelogs (Linear, Vercel).
 *
 * Used in two places, with the same component:
 *   • `/app/projects` - when the project's `layout === 'changelog'`. Shows the
 *     "Add entry" toolbar action; clicking an entry opens DocumentPage editor.
 *   • Public team page - for projects that are publicly shared. `readOnly`
 *     hides the toolbar and disables click-through (no editor for guests).
 *
 * An entry may carry `translations` ({ sk: { title, content }, ... }) shown in the
 * reader's language, and a `published_at` release date it is dated and sorted by.
 *
 * Entries themselves are full-fledged Documents - they share the autosave,
 * full-width toggle, breadcrumb and trash plumbing already wired up for the
 * other 5 document types.
 */

// The release date of an entry (published_at), else when it was last changed.
export function effectiveDate(doc) {
  return doc.published_at || doc.updated_at || doc.created_at;
}

/** An entry's title and content in the reader's language ("pt-BR", then "pt"), else the base (English) text. */
export function entryText(doc, language) {
  const translations = doc.translations || {};
  const lang = (language || '').trim();
  const found = translations[lang] || translations[lang.split('-')[0]] || {};
  return { title: found.title || doc.title, content: found.content || doc.content };
}

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
}

function formatMonth(d, locale) {
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
}

const ChangelogView = ({
  documents = [],
  projectId,
  teamId,
  readOnly = false,
  onCreated, // optional refresh hook for parent
}) => {
  const { t, i18n } = useTranslation();
  const { formatDate } = useDateTime();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const groups = useMemo(() => {
    const sorted = documents
      .slice()
      .sort((a, b) => (effectiveDate(b) || '').localeCompare(effectiveDate(a) || ''));
    const map = new Map();
    for (const doc of sorted) {
      const raw = effectiveDate(doc);
      if (!raw) continue;
      const d = new Date(raw);
      const key = monthKey(d);
      if (!map.has(key)) map.set(key, { date: d, items: [] });
      map.get(key).items.push(doc);
    }
    return Array.from(map.entries()).map(([key, value]) => ({ key, ...value }));
  }, [documents]);

  const handleAddEntry = async () => {
    if (readOnly || creating) return;
    setCreating(true);
    try {
      const res = await documentsApi.create({
        type: 'changelog',
        title: '',
        project_id: projectId || null,
        team_id: teamId || null,
      });
      const newId = res?.data?.id;
      if (newId) {
        onCreated?.();
        navigate(`/app/documents/${newId}`);
      }
    } catch (err) {
      // 402/403 → tier or feature gate. The api interceptor already raises an
      // upgrade prompt event for 402; surface a toast for other failures.
      if (err.response?.status !== 402) {
        toast.error(
          err.response?.data?.detail
            || t('projects.changelog_add_failed', { defaultValue: 'Could not add entry.' }),
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEntry = (doc) => {
    if (readOnly) return;
    navigate(`/app/documents/${doc.id}`);
  };

  const Toolbar = readOnly ? null : (
    <div className="flex items-center justify-between mb-6">
      <p className="text-xs text-muted-foreground">
        {t('projects.changelog_subtitle', {
          defaultValue: 'Publish what shipped, what changed, what you fixed.',
        })}
      </p>
      <Button type="button" size="sm" onClick={handleAddEntry} disabled={creating}>
        <Plus className="w-3.5 h-3.5" />
        {t('projects.changelog_add_entry', { defaultValue: 'Add entry' })}
      </Button>
    </div>
  );

  if (documents.length === 0) {
    return (
      <div>
        {Toolbar}
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-lg">
          <History className="w-10 h-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            {t('projects.changelog_empty', { defaultValue: 'No changelog entries yet.' })}
          </p>
          {!readOnly && (
            <>
              <p className="text-xs text-muted-foreground/70 mt-1 mb-4">
                {t('projects.changelog_empty_hint', {
                  defaultValue: 'Publish a release note, a bug fix, or a feature update.',
                })}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddEntry}
                disabled={creating}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('projects.changelog_add_entry', { defaultValue: 'Add entry' })}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {Toolbar}
      {groups.map(group => (
        <section key={group.key}>
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-4">
            {formatMonth(group.date, i18n.language)}
          </h2>
          <div className="space-y-6">
            {group.items.map(doc => {
              const date = new Date(effectiveDate(doc));
              const text = entryText(doc, i18n.language);
              const title = text.title || t('common.untitled', { defaultValue: 'Untitled' });
              return (
                <article
                  key={doc.id}
                  onClick={() => handleOpenEntry(doc)}
                  className={`grid grid-cols-[80px_1fr] sm:grid-cols-[120px_1fr] gap-4 sm:gap-6 group ${
                    readOnly ? '' : 'cursor-pointer'
                  }`}
                >
                  <div className="text-xs text-muted-foreground/70 font-medium pt-1">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 opacity-60" />
                      {formatDate ? formatDate(date) : date.toLocaleDateString(i18n.language)}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h3
                      className={`text-base sm:text-lg font-semibold text-foreground transition-colors ${
                        readOnly ? '' : 'group-hover:text-amber-700 dark:group-hover:text-amber-400'
                      }`}
                    >
                      {title}
                    </h3>
                    {text.content && (
                      <div className="mt-2 text-sm text-muted-foreground leading-relaxed pointer-events-none">
                        <BlockNoteEditor
                          key={`${doc.id}-${i18n.language}`}
                          content={text.content}
                          editable={false}
                          minHeight="0"
                        />
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ChangelogView;
